<script setup lang="ts">
import { onMounted, ref, Component } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import type {
  EvaluationType,
  EvaluationListType,
} from "@/common/types/evaluation";

import { PanelTheme } from "../";

import {
  baseInput,
  baseConfirm,
  baseAvatar,
  svgIcon,
  tabMenu,
  iconMenu,
  cardEvaluation,
} from "@/common/components";

interface TabListType {
  [key: string]: {
    key: string;
    icon: string;
    label: string;
    component: Component;
    props?: any;
  };
}

const { t } = useI18n();
const router = useRouter();

const emit = defineEmits([
  "update-data",
  "add",
  "input-search",
  "search",
  "select-tab",
]);

const props = defineProps<{
  accountId: string;
  evaluationId: string | null;
  evaluation: EvaluationType | null;
  evaluations: EvaluationListType[];
  tabList: TabListType;
  menuList: any;
  isEdit: boolean;
}>();

const selectedTab = ref<string | null>(null);

function selectTab(tab: string) {
  selectedTab.value = tab;
}

function back() {
  router.push(`/${props.accountId}/evaluate`);
}

onMounted(() => {
  selectTab("info");
});
</script>

<template>
  <panel-theme>
    <div class="header">
      <template v-if="evaluation">
        <div class="header-title">
          <base-avatar
            size="1.5em"
            :url="evaluation.company?.avatar?.small"
          />
          <div class="infos">
            <div class="code">
              {{ evaluation.code }}
            </div>
            <template v-if="evaluation.company">
              <div class="company">
                {{ evaluation.company?.name }}
              </div>
            </template>
          </div>
        </div>
      </template>

      <div class="header-actions">
        <button type="button" class="btn btn-back h-8 w-8 p-0" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </div>

    <div class="content">
      <div class="evaluation">
        <tab-menu
          :tabs="menuList"
          :active="selectedTab"
          @select:tab="selectTab($event)"
        >
          <template #content>
            <template v-if="selectedTab">
              <component
                :is="tabList[selectedTab]?.component"
                v-bind="tabList[selectedTab]?.props"
                :is-edit="isEdit"
                :account-id="accountId"
                :evaluation-id="evaluation?.evaluation_id"
                :data="evaluation"
                @update-data="$emit('update-data')"
              />
            </template>
          </template>
        </tab-menu>
      </div>
    </div>
  </panel-theme>
</template>

<style lang="scss" scoped>
.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 3rem;
  margin-block-end: 1rem;
  padding-inline: 0.5rem;
  padding-block: 0.5rem;
  border-radius: 0.5rem;
  background: var(--white);

  &-title {
    flex: 8;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;

    .infos {
      display: flex;
      flex-direction: column;

      .code {
        color: var(--text);
        font-size: 1rem;
        font-weight: bold;
        text-transform: uppercase;
      }

      .company {
        color: var(--text);
        font-size: 0.875rem;
        text-transform: uppercase;
      }
    }
  }

  &-actions {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
  }
}

.content {
  display: flex;
  flex-direction: row;
  gap: 1rem;

  .evaluation {
    display: flex;
    flex: 1;
    flex-direction: column;
    height: calc(80.5dvh - 2.3rem);
    overflow-y: auto;
  }
}
</style>